import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Play, Pause, ShoppingCart, Layers } from "lucide-react";
import { usePlayer } from "@/context/PlayerContext";
import { coverUrl, fmtDuration, queueAfter } from "@/lib/api";
import BizumModal from "@/components/BizumModal";
import { GenrePill } from "@/components/GenrePill";
import { TrackStats } from "@/components/TrackStats";
import { StarRating } from "@/components/StarRating";

export default function TrackCard({ track, queue = [], config }) {
  const p = usePlayer();
  const [bizumOpen, setBizumOpen] = useState(false);
  const isCurrent = p?.current?.id === track.id;
  const isPlaying = isCurrent && p?.isPlaying;

  const handlePlay = (e) => {
    e.preventDefault();
    e.stopPropagation();
    p.playTrack(track, queueAfter(queue, track.id));
  };

  return (
    <>
      <div
        data-testid={`track-card-${track.id}`} onMouseEnter={() => p.prefetch?.(track)}
        className="group relative bg-[#16161D] border border-white/5 rounded-xl overflow-hidden card-glow transition-all duration-300 flex flex-col"
      >
        {track.cover_path && (
          <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
            <img src={coverUrl(track.cover_path)} alt="" className="w-full h-full object-cover blur-2xl scale-125 opacity-70" />
            <div className="absolute inset-0 bg-gradient-to-b from-[#0d0d12]/40 via-[#0d0d12]/60 to-[#0d0d12]/85"></div>
          </div>
        )}
        <div className="relative aspect-square bg-neutral-900 overflow-hidden">
          {track.cover_path ? (
            <img src={coverUrl(track.cover_path)} alt={track.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-red-900/40 via-black to-black flex items-center justify-center">
              <span className="font-display font-black text-4xl text-red-600/40">{track.title?.[0] || "?"}</span>
            </div>
          )}
          <button
            data-testid={`track-play-button-${track.id}`}
            onClick={handlePlay}
            className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity"
            aria-label="Play"
          >
            <span className="w-11 h-11 rounded-full btn-red flex items-center justify-center shadow-[0_0_25px_rgba(229,9,20,0.6)]">
              {isPlaying ? <Pause className="w-5 h-5 text-white" /> : <Play className="w-5 h-5 text-white ml-0.5" />}
            </span>
          </button>

          {isCurrent && (
            <div className="absolute top-2 left-2 bg-black/70 backdrop-blur px-1.5 py-1 rounded flex items-center gap-0.5">
              <span className="eq-bar h-2.5"></span><span className="eq-bar h-2.5"></span>
              <span className="eq-bar h-2.5"></span><span className="eq-bar h-2.5"></span>
            </div>
          )}

        </div>

        <div className="relative p-4 sm:p-5 flex-1 min-w-0 flex flex-col gap-2">
          <Link to={`/track/${track.id}`} className="block">
            <h3 className="font-display font-bold text-sm sm:text-base uppercase tracking-tight text-white leading-tight line-clamp-2 hover:text-red-500 transition-colors">
              {track.title}
            </h3>
            <p className="text-xs text-white mt-1 line-clamp-1 flex items-center gap-2">{track.artist}
              {(track.versions || []).length > 0 && <span className="inline-flex items-center gap-1 text-pink-400 font-semibold text-[10px]" data-testid={`track-card-versions-${track.id}`}><Layers className="w-3 h-3" />{track.versions.length + 1} versiones</span>}
            </p>
          </Link>

          <StarRating track={track} />
          {config?.show_stats && <TrackStats track={track} />}
          <div className="flex flex-wrap gap-1">
            {track.duration && <Pill testid={`track-duration-${track.id}`}>{fmtDuration(track.duration)}</Pill>}
            {track.bpm && <Pill red>{track.bpm} BPM</Pill>}
            {track.genre && <GenrePill genre={track.genre} />}
            {(track.tags || []).map((tag) => <Pill key={tag}>{tag}</Pill>)}
          </div>

          <div className="mt-auto pt-3 flex items-stretch gap-2">
            <button onClick={handlePlay} data-testid={`track-listen-button-${track.id}`} title={isPlaying ? "Pausar" : "Escuchar"}
              className="shrink-0 w-10 h-10 flex items-center justify-center rounded-full btn-red text-white">
              {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
            </button>
            <span data-testid={`track-price-badge-${track.id}`}
              className={`shrink-0 flex items-center px-3 text-xs font-mono uppercase tracking-widest rounded-full border ${
                track.price === 0
                  ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30"
                  : "bg-red-600/25 text-red-400 border-red-600/40"
              }`}>
              {track.price === 0 ? "Free" : `€${track.price.toFixed(2)}`}
            </span>
            {track.price > 0 && (
              <button onClick={() => setBizumOpen(true)} data-testid={`track-buy-button-${track.id}`} title="Comprar con PayPal"
                className="flex-1 flex items-center justify-center gap-2 px-4 rounded-full bg-white/5 hover:bg-red-600/20 border border-white/10 hover:border-red-600/50 text-sm font-semibold text-white transition-all">
                <ShoppingCart className="w-4 h-4" /> Comprar
              </button>
            )}
          </div>
        </div>
      </div>

      <BizumModal open={bizumOpen} onClose={() => setBizumOpen(false)} item={track} itemType="track" config={config} />
    </>
  );
}

function Pill({ children, red, testid }) {
  return (
    <span data-testid={testid} className={`font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${
      red ? "text-sky-400 border-sky-800/50 bg-sky-950/40" : "text-white/60 border-white/10"
    }`}>{children}</span>
  );
}
